import { useState, type FormEvent } from "react";
import SiteNav from "@/components/feature/SiteNav";
import SiteFooter from "@/components/feature/SiteFooter";
import Reveal from "@/components/base/Reveal";

type Topic = "General" | "Press" | "Partnership" | "Support";

export default function ContactPage() {
  const [topic, setTopic] = useState<Topic>("General");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const [messageLen, setMessageLen] = useState(0);

  const topics: Topic[] = ["General", "Press", "Partnership", "Support"];

  const contactBlocks = [
    {
      k: "Studio",
      title: "Paris",
      lines: ["48 Rue de Rivoli", "75004 Paris, France"],
    },
    {
      k: "Studio",
      title: "Kyoto",
      lines: ["7-3 Karasumaru-cho", "Nakagyō-ku, Kyoto 604-8221"],
    },
    {
      k: "Direct",
      title: "hello@topbud.studio",
      lines: ["Mon — Fri, 10:00 — 18:00 CET", "Reply within two working days"],
    },
  ];

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formEl = e.currentTarget;
    const fd = new FormData(formEl);

    const hp = String(fd.get("website_alt") || "").trim();
    if (hp) {
      setStatus("success");
      formEl.reset();
      setMessageLen(0);
      return;
    }
    fd.delete("website_alt");

    setStatus("loading");
    setErrorMsg("");

    try {
      const body = new URLSearchParams();
      fd.forEach((v, k) => body.append(k, String(v)));

      const res = await fetch("https://readdy.ai/api/form/d9fjsqsbl0ahdbsf9rmg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const text = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } = {};
      try {
        parsed = JSON.parse(text);
      } catch {
        parsed = {};
      }
      const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || text || "";
      const isSpamMsg = /spam/i.test(serverMsg);

      if (res.ok && parsed?.code === "OK" && !isSpamMsg) {
        setStatus("success");
        formEl.reset();
        setMessageLen(0);
      } else {
        setStatus("error");
        setErrorMsg(serverMsg || "Something went wrong. Please try again.");
      }
    } catch {
      setStatus("error");
      setErrorMsg("Network error. Please try again.");
    }
  };

  return (
    <main className="min-w-[1024px] w-full min-h-screen bg-background-50 text-foreground-950">
      <div className="flex flex-col">
        <SiteNav />

        {/* Header */}
        <section
          id="contact-hero"
          className="w-full px-6 md:px-10 pt-16 md:pt-24 pb-16 md:pb-20"
        >
          <Reveal y={30}>
            <div className="flex items-end justify-between mb-16 md:mb-20">
              <p className="font-label text-[11px] tracking-[0.22em] uppercase text-foreground-500">
                <span>Contact — 03</span>
              </p>
              <p className="font-label text-[11px] tracking-[0.22em] uppercase text-foreground-500 hidden md:block">
                <span>Write to the studio</span>
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-14 items-end">
            <Reveal delay={120} y={40}>
              <h1
                className="font-heading font-semibold leading-[0.92] tracking-[-0.03em] text-foreground-950"
                style={{ fontSize: "clamp(56px, 9vw, 140px)" }}
              >
                Get in <br />
                <span className="italic font-normal text-foreground-700">
                  touch.
                </span>
              </h1>
            </Reveal>
            <Reveal delay={260} y={40}>
              <div className="max-w-md">
                <p className="text-[13px] leading-[1.75] text-foreground-700 text-justify">
                  We are a small studio, so we read every letter ourselves. Tell
                  us what you&apos;re working on, or simply say hello — no form
                  is too small, and no idea is too quiet.
                </p>
                <div className="mt-8 flex items-center gap-4">
                  <span className="h-px w-10 bg-foreground-400" />
                  <span className="font-label text-[11px] tracking-[0.22em] uppercase text-foreground-600">
                    Reply within 2 working days
                  </span>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        {/* Content grid */}
        <section className="w-full px-6 md:px-10 pb-24 md:pb-32">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-10 lg:gap-14 items-start">
            {/* LEFT — studio info + image */}
            <div className="flex flex-col gap-6">
              <Reveal y={60} duration={1000}>
                <div className="relative w-full aspect-[4/5] rounded-lg overflow-hidden bg-background-100 border border-background-200">
                  <img
                    src="https://readdy.ai/api/search-image?query=Editorial%20still%20life%20of%20a%20quiet%20design%20studio%20interior%20with%20warm%20paper%2C%20a%20small%20black%20AI%20device%2C%20a%20cup%20of%20coffee%2C%20a%20folded%20letter%2C%20soft%20warm%20side%20light%20from%20a%20window%2C%20muted%20monochrome%20warm%20palette%2C%20editorial%20fine%20art%20photography%2C%20refined%20negative%20space%2C%20calm%20atmosphere&width=1200&height=1500&seq=topbud-contact-main&orientation=portrait"
                    alt="Topbud studio"
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute left-4 top-4 flex items-center gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-accent-500" />
                    <span className="font-label text-[10px] tracking-[0.22em] uppercase text-background-50">
                      The studio · 04
                    </span>
                  </div>
                  <span className="absolute right-4 bottom-4 font-label text-[10px] tracking-[0.22em] uppercase text-background-50/80">
                    Fig. 04 / Room
                  </span>
                </div>
              </Reveal>

              <div className="grid grid-cols-1 gap-4">
                {contactBlocks.map((c, i) => (
                  <Reveal key={c.title} delay={150 + i * 120} y={30}>
                    <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
                      <p className="font-label text-[10px] tracking-[0.22em] uppercase text-foreground-500">
                        {c.k}
                      </p>
                      <p className="mt-3 font-heading text-[22px] leading-none tracking-[-0.02em] text-foreground-950">
                        {c.title}
                      </p>
                      <div className="mt-4 space-y-1">
                        {c.lines.map((line) => (
                          <p key={line} className="text-[13px] leading-[1.6] text-foreground-700">
                            {line}
                          </p>
                        ))}
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>

            {/* RIGHT — form */}
            <Reveal delay={200} y={40}>
              <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-10">
                <div className="flex items-center justify-between">
                  <p className="font-label text-[11px] tracking-[0.22em] uppercase text-foreground-500">
                    Write to us
                  </p>
                  <p className="font-label text-[11px] tracking-[0.22em] uppercase text-foreground-400">
                    Step 01 / 01
                  </p>
                </div>

                <h2 className="mt-6 font-heading font-semibold text-[32px] md:text-[42px] leading-[1.02] tracking-[-0.02em] text-foreground-950 max-w-md">
                  A letter to the <br />
                  <span className="italic font-normal text-foreground-700">
                    design office.
                  </span>
                </h2>

                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  id="topbud-contact-form"
                  className="mt-10"
                  noValidate
                >
                  {/* Honeypot */}
                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="hp-field-alt"
                  />

                  {/* Topic pills */}
                  <div>
                    <p className="font-label text-[11px] tracking-[0.22em] uppercase text-foreground-500">
                      Topic
                    </p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {topics.map((t) => {
                        const active = t === topic;
                        return (
                          <button
                            key={t}
                            type="button"
                            onClick={() => setTopic(t)}
                            className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-2 border font-label text-[11px] tracking-[0.18em] uppercase transition-colors duration-200 ${
                              active
                                ? "border-foreground-950 bg-foreground-950 text-background-50"
                                : "border-background-300 text-foreground-800 hover:border-foreground-500"
                            }`}
                          >
                            {t}
                          </button>
                        );
                      })}
                    </div>
                    <input type="hidden" name="topic" value={topic} />
                  </div>

                  {/* Name + Email */}
                  <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="flex flex-col">
                      <label
                        htmlFor="contact-name"
                        className="font-label text-[11px] tracking-[0.22em] uppercase text-foreground-500"
                      >
                        Name
                      </label>
                      <input
                        id="contact-name"
                        type="text"
                        name="name"
                        required
                        placeholder="Your name"
                        className="mt-3 bg-transparent border-b border-foreground-300 outline-none focus:border-foreground-950 text-[15px] text-foreground-950 placeholder:text-foreground-400 py-2 transition-colors duration-200"
                      />
                    </div>
                    <div className="flex flex-col">
                      <label
                        htmlFor="contact-email"
                        className="font-label text-[11px] tracking-[0.22em] uppercase text-foreground-500"
                      >
                        Email
                      </label>
                      <input
                        id="contact-email"
                        type="email"
                        name="email"
                        required
                        placeholder="you@studio.com"
                        className="mt-3 bg-transparent border-b border-foreground-300 outline-none focus:border-foreground-950 text-[15px] text-foreground-950 placeholder:text-foreground-400 py-2 transition-colors duration-200"
                      />
                    </div>
                  </div>

                  {/* Company + Subject */}
                  <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="flex flex-col">
                      <label
                        htmlFor="contact-company"
                        className="font-label text-[11px] tracking-[0.22em] uppercase text-foreground-500"
                      >
                        Studio / Company (optional)
                      </label>
                      <input
                        id="contact-company"
                        type="text"
                        name="company"
                        placeholder="Where you write from"
                        className="mt-3 bg-transparent border-b border-foreground-300 outline-none focus:border-foreground-950 text-[15px] text-foreground-950 placeholder:text-foreground-400 py-2 transition-colors duration-200"
                      />
                    </div>
                    <div className="flex flex-col">
                      <label
                        htmlFor="contact-subject"
                        className="font-label text-[11px] tracking-[0.22em] uppercase text-foreground-500"
                      >
                        Subject
                      </label>
                      <input
                        id="contact-subject"
                        type="text"
                        name="subject"
                        required
                        placeholder="A short line"
                        className="mt-3 bg-transparent border-b border-foreground-300 outline-none focus:border-foreground-950 text-[15px] text-foreground-950 placeholder:text-foreground-400 py-2 transition-colors duration-200"
                      />
                    </div>
                  </div>

                  {/* Message */}
                  <div className="mt-8 flex flex-col">
                    <div className="flex items-center justify-between">
                      <label
                        htmlFor="contact-message"
                        className="font-label text-[11px] tracking-[0.22em] uppercase text-foreground-500"
                      >
                        Message
                      </label>
                      <span className="font-label text-[10px] tracking-[0.22em] uppercase text-foreground-500 tabular-nums">
                        {messageLen} / 500
                      </span>
                    </div>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={5}
                      onChange={(e) => setMessageLen(e.target.value.length)}
                      placeholder="Tell us, quietly, what you have in mind."
                      className="mt-3 bg-transparent border-b border-foreground-300 outline-none focus:border-foreground-950 text-[15px] text-foreground-950 placeholder:text-foreground-400 py-2 resize-none transition-colors duration-200"
                    />
                  </div>

                  {/* Actions */}
                  <div className="mt-10 flex items-center justify-between">
                    <p className="font-label text-[10px] tracking-[0.22em] uppercase text-foreground-500 max-w-xs">
                      By writing, you agree to be answered — once, personally.
                    </p>
                    <button
                      type="submit"
                      disabled={status === "loading"}
                      className="cursor-pointer whitespace-nowrap inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 dark:text-foreground-950 text-[11px] font-label tracking-[0.18em] uppercase hover:bg-primary-600 transition-colors duration-200 disabled:opacity-60"
                    >
                      <span>{status === "loading" ? "Sending" : "Send letter"}</span>
                      <i className="ri-arrow-right-up-line text-[13px]" />
                    </button>
                  </div>

                  {/* Status */}
                  <div className="mt-6 min-h-[16px]">
                    {status === "success" && (
                      <p className="font-label text-[11px] tracking-[0.18em] uppercase text-accent-600">
                        <span>Thank you — the letter is on our desk.</span>
                      </p>
                    )}
                    {status === "error" && (
                      <p className="font-label text-[11px] tracking-[0.18em] uppercase text-accent-700">
                        <span>{errorMsg}</span>
                      </p>
                    )}
                  </div>
                </form>
              </div>
            </Reveal>
          </div>
        </section>
      </div>

      <SiteFooter />
    </main>
  );
}